import { displayName } from "../domain/modules";
import { partVars } from "../lib/partColors";

// Module glyphs are our own SVG files in src/assets/glyphs/<module-id>.svg (drawn with currentColor).
// Bundled as raw text so they inherit colour; a module without its own glyph gets generic.svg, or the
// neutral port glyph when that file is missing too.
const FILES = import.meta.glob<string>("../assets/glyphs/*.svg", { query: "?raw", import: "default", eager: true });
const GLYPHS: Record<string, string> = Object.fromEntries(
  Object.entries(FILES).map(([path, svg]) => [path.replace(/^.*\/([^/]+)\.svg$/, "$1"), svg]),
);

export function hasGlyph(moduleId: string): boolean {
  return moduleId in GLYPHS && moduleId !== "generic";
}

/** Neutral port glyph: a rounded terminal on the horizontal channel, used when a module has no glyph of its own. */
export function PortGlyph({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="8" y="8" width="8" height="8" rx="2.5" />
      <path d="M1 12h7M16 12h7" />
    </svg>
  );
}

/**
 * Functional glyph for a module. decorative (default) hides it from assistive tech because the module
 * name is always printed next to it; pass label to make it an image with a name.
 */
export function Glyph({ module, size = 24, tile, label, className }: { module: string; size?: number; tile?: boolean; label?: string; className?: string }) {
  const svg = GLYPHS[module] ?? GLYPHS["generic"];
  const cls = `glyph${tile ? " tile" : ""}${className ? ` ${className}` : ""}`;
  const a11y = label ? { role: "img", "aria-label": label } : { "aria-hidden": true as const };
  const box = tile ? size * 1.6 : size;
  const style = { width: box, height: box, ...partVars(module) } as React.CSSProperties;
  if (!svg) {
    return <span className={cls} style={style} {...a11y} title={label ?? displayName(module)}><PortGlyph size={size} /></span>;
  }
  return <span className={cls} style={style} {...a11y} dangerouslySetInnerHTML={{ __html: svg }} />;
}

/** Raw SVG markup of a module glyph (currentColor strokes), or null. Used by the canvas scene. */
export function glyphSvg(module: string): string | null {
  return GLYPHS[module] ?? null;
}
